import { useEffect, useState } from "react";
import type { Node } from "@xyflow/react";
import { frameNodeSize } from "@/lib/frame-node-geometry";
import { useCanvasStore } from "@/stores/canvas-store";
import { useCanvasTimePageStore } from "@/stores/canvas-time-page-store";
import type { FrameData } from "@/lib/frame-data";

function timePageInputsChanged(
  previous: Node<FrameData>[],
  next: Node<FrameData>[],
): boolean {
  if (previous.length !== next.length) return true;
  return previous.some((node, index) => {
    const nextNode = next[index];
    if (!nextNode || node.id !== nextNode.id) return true;
    if (node === nextNode) return false;
    const previousSize = frameNodeSize(node);
    const nextSize = frameNodeSize(nextNode);
    return (
      node.hidden !== nextNode.hidden ||
      previousSize.width !== nextSize.width ||
      previousSize.height !== nextSize.height ||
      node.data.spaceId !== nextNode.data.spaceId ||
      node.data.frameType !== nextNode.data.frameType ||
      node.data.createdAt !== nextNode.data.createdAt ||
      node.data.metadata?.hidden !== nextNode.data.metadata?.hidden ||
      node.data.metadata?.pinned !== nextNode.data.metadata?.pinned
    );
  });
}

/** Subscribes only to fields that can change chronological page membership. */
export function useCanvasTimePageNodes(): Node<FrameData>[] {
  const resizeInProgress = useCanvasTimePageStore(
    (state) => state.resizingFrameIds.size > 0,
  );
  const [pageNodes, setPageNodes] = useState(
    () => useCanvasStore.getState().nodes,
  );

  useEffect(() => {
    const unsubscribe = useCanvasStore.subscribe((state, previous) => {
      if (state.nodes === previous.nodes) return;
      if (useCanvasTimePageStore.getState().resizingFrameIds.size > 0) return;
      setPageNodes((current) =>
        timePageInputsChanged(current, state.nodes) ? state.nodes : current,
      );
    });
    if (resizeInProgress) return unsubscribe;
    const current = useCanvasStore.getState().nodes;
    setPageNodes((previous) =>
      timePageInputsChanged(previous, current) ? current : previous,
    );
    return unsubscribe;
  }, [resizeInProgress]);

  return pageNodes;
}
